import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Loader2, Calendar, User, Eye } from "lucide-react";

function useBlog() {
  return useQuery({
    queryKey: ["blog"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/blog", { params: { page: 1, limit: 24 } });
      return data.posts || data.blog || data || [];
    },
    staleTime: 5 * 60_000,
  });
}

export default function BlogListPage() {
  const q = useBlog();
  const posts = q.data || [];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Blog" description="Marketplace tips, stories and product news from Allsale Classified." />
      <Breadcrumbs items={[{ label: "Blog" }]} />

      <h1 className="mt-6 font-display text-4xl font-black text-slate-900">Allsale Blog</h1>
      <p className="mt-2 text-slate-500">Tips, guides and stories from the Kiwi marketplace.</p>

      {q.isLoading && <div className="mt-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

      {!q.isLoading && posts.length === 0 && (
        <div className="mt-12 rounded-2xl border border-dashed border-soft bg-white p-14 text-center text-slate-500">
          Nothing posted yet — check back soon.
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((p) => (
          <Link
            key={p.slug || p.id}
            to={`/blog/${p.slug}`}
            className="group block bg-white rounded-xl shadow-card hover:shadow-card-hover overflow-hidden card-lift"
            data-testid={`blog-card-${p.slug}`}
          >
            {p.cover_image_url && (
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            )}
            <div className="p-5">
              {p.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {p.tags.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] rounded-full px-2 py-0.5">{t}</span>
                  ))}
                </div>
              )}
              <h2 className="font-display text-lg font-bold text-slate-900 clamp-2 group-hover:text-[color:var(--brand-blue)] transition-colors">{p.title}</h2>
              {p.excerpt && <p className="mt-2 text-sm text-slate-500 clamp-3">{p.excerpt}</p>}
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                {p.author_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{p.author_name}</span>}
                {p.published_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.published_at).toLocaleDateString("en-NZ")}</span>}
                {p.views != null && <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{p.views}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
